import React from 'react';
import {
  Button,
  Col,
  IconWrapper,
  Image,
  MultiLineEllipsisText,
  PriorityBadge,
  Row,
  Tag,
  Typography,
} from 'components';
import { ClientTypeIcon, EditIconButton, GlobeIcon, LanguageIcon, VerifiedIcon } from 'components/common/icons';
import { ClientDetailsUpdate } from 'pages/admin/clients/client/details/edit';
import styles from 'pages/admin/clients/client/details/styles.module.scss';
import { useDetails } from 'pages/admin/clients/client/details/useDetails';
import { HasAccess } from 'components/common/accessControl';
import { PERMISSIONS } from 'constants/permissions';

const { Text } = Typography;

export const Details = React.memo(() => {
  const { isOpen, typeDetails, onModalClose, onModalOpen, clientDetails, countryDetails, languageDetails } =
    useDetails();
  return (
    <>
      <Row gutter={[0, 16]}>
        <Col span={24} className={styles.main_info}>
          <Row justify={'space-between'} wrap={false}>
            <Col span={20}>
              <Row gutter={[0, 4]}>
                {clientDetails.files?.LOGO && (
                  <Col className={styles.image_wrapper} span={24}>
                    <Image src={clientDetails.files.LOGO} preview={false} alt={'Logo'} />
                  </Col>
                )}
                <Col span={24}>
                  <Text className={'font-size-12'} type={'secondary'}>
                    ID: {clientDetails.uuid}
                  </Text>
                </Col>
                <Col span={24} className={styles.legal_name}>
                  <Row align={'middle'} wrap={false}>
                    <Text title={clientDetails.name} className={styles.name}>
                      {clientDetails.name}
                    </Text>
                    {clientDetails.verified && <VerifiedIcon />}
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col>
              <HasAccess role={PERMISSIONS.clients.client.write}>
                <Button type={'link'} icon={<EditIconButton />} onClick={onModalOpen} />
              </HasAccess>
            </Col>
          </Row>
          <Row gutter={[0, 4]} className={'margin-t-4'}>
            <Col span={24}>
              <Row style={{ gap: '9px' }}>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {clientDetails.formattedCreatedDate}
                </Text>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {clientDetails.formattedLastModifiedDate}
                </Text>
                {clientDetails.formattedLastActivityDate && (
                  <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                    Last Activity Date: {clientDetails.formattedLastActivityDate}
                  </Text>
                )}
              </Row>
            </Col>
            <Col span={24} className={'margin-t-4'}>
              <PriorityBadge tooltip={clientDetails.priorityRationale} type={clientDetails.priorityLevel} />
            </Col>
          </Row>
        </Col>
        <Col span={24} className={styles.additional_info}>
          <Row gutter={[0, 14]}>
            {clientDetails.webSiteUrl && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <GlobeIcon />
                  <Button
                    style={{ padding: 0 }}
                    title={clientDetails.webSiteUrl}
                    type="link"
                    href={
                      clientDetails.webSiteUrl?.startsWith('http')
                        ? clientDetails.webSiteUrl
                        : `https://${clientDetails.webSiteUrl}`
                    }
                    target="_blank"
                  >
                    <Text className={styles.cut_long_text} type={'secondary'}>
                      {clientDetails.webSiteUrl}
                    </Text>
                  </Button>
                </Row>
              </Col>
            )}
            {countryDetails.commonName && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  {countryDetails.flagLocation && <IconWrapper src={countryDetails.flagLocation} />}
                  <Text type={'secondary'}>{countryDetails.commonName}</Text>
                </Row>
              </Col>
            )}
            {languageDetails.label && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <LanguageIcon />
                  <Text type={'secondary'}>{languageDetails.label}</Text>
                </Row>
              </Col>
            )}
            {typeDetails.label && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <ClientTypeIcon />
                  <Text type={'secondary'}>{typeDetails.label}</Text>
                </Row>
              </Col>
            )}
            {/*TODO system generated tags*/}
          </Row>
        </Col>
        <Col span={24} className={styles.manual_tags}>
          <Row className={styles.tag_container}>
            {clientDetails.manualTags?.map((_tag: string, index: number) => (
              <Tag title={_tag} key={index + _tag} className={'custom-tag'}>
                {_tag}
              </Tag>
            ))}
          </Row>
        </Col>
        <Col span={24}>
          <MultiLineEllipsisText subTitle={'Additional Notes:'} text={clientDetails.additionalNotes} />
        </Col>
      </Row>
      <ClientDetailsUpdate isOpen={isOpen} onCancel={onModalClose} />
    </>
  );
});
